import { useMemo } from "react";
import {
  SystemEntityCollections,
  SystemEntityProofreadingStatus,
  SystemLanguages,
} from "../../../shared/enums/shared-enums";
import {
  SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS,
  SystemTeamContributionTypes,
  VersionTeamConnection,
} from "../../../types/versionTeam";
import { Team } from "../../../types/team";
import { Version } from "../../../types/version";
import { DEFAULT_INTELLEXUS_DATE } from "../../../types/intellexusDate";
import { initMetadata } from "../../../controllers/formController";
import { MRT_ColumnDef } from "material-react-table";
import {
  chineseVersionTeamColumns,
  tibetanVersionTeamColumns,
} from "../../../common/entities/VersionTeamColumns";

export const languageBasedTypes = (
  language: VersionTeamConnection["system_language_type"]
): SystemTeamContributionTypes[] => {
  switch (language) {
    case SystemLanguages.tibetan:
      return [
        SystemTeamContributionTypes.TRANSLATORS,
        SystemTeamContributionTypes.REVISORS_OF_TIBETAN_TRANSLATION,
      ];
    case SystemLanguages.chinese:
      return [
        SystemTeamContributionTypes.EARLIEST_CHINESE_TRANSLATION,
        SystemTeamContributionTypes.CHINESE_TRANSLATION_CLOSER_TO_TIBETAN_WORK,
        SystemTeamContributionTypes.CHINESE_TRANSLATION_CLOSER_TO_INDIC_WORK,
      ];
  }
};

const useVersionTeamConnectionLogic = (
  from: Version | null,
  to: Team | null,
  teamContributionType: SystemTeamContributionTypes,
  language?: string
) => {
  const systemLanguageType = useMemo<
    VersionTeamConnection["system_language_type"] | null
  >(() => {
    if (
      !Object.values(SystemLanguages).includes(language as SystemLanguages) ||
      (language !== SystemLanguages.tibetan &&
        language !== SystemLanguages.chinese)
    ) {
      return null;
    }
    return language as VersionTeamConnection["system_language_type"];
  }, [language]);

  const metadataFieldNames = useMemo<Array<keyof VersionTeamConnection>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return [
      "date",
      "place_identification_status",
      "revision",
      "title",
      "taisho_catalog_no",
      "source_language",
    ];
  }, [systemLanguageType]);

  const teamContributionPlaceIdentificationStatusEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
        return "team_contribution_place_identification_status";
      case SystemLanguages.chinese:
        return "";
    }
  }, [systemLanguageType]);

  const chineseSourceLanguageEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
        return "";
      case SystemLanguages.chinese:
        return "chinese_source_language";
    }
  }, [systemLanguageType]);

  const revisionEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return teamContributionType ===
      SystemTeamContributionTypes.REVISORS_OF_TIBETAN_TRANSLATION
      ? "revision"
      : "";
  }, [systemLanguageType, teamContributionType]);

  const userEnums = useMemo<string[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return [
      teamContributionPlaceIdentificationStatusEnum,
      chineseSourceLanguageEnum,
      revisionEnum,
    ].filter(Boolean);
  }, [
    systemLanguageType,
    teamContributionPlaceIdentificationStatusEnum,
    chineseSourceLanguageEnum,
    revisionEnum,
  ]);

  const defaultVersionTeam = useMemo<VersionTeamConnection>(() => {
    if (systemLanguageType === null) {
      throw new Error("Invalid system language type");
    }
    return {
      _from: from?._id || "",
      _to: to?._id || "",
      _id: "",
      _key: "",
      _rev: "",
      system_team_contribution_type: teamContributionType,
      system_language_type: systemLanguageType,
      date: DEFAULT_INTELLEXUS_DATE,
      place_identification_status: "",
      revision: [],
      title: "",
      taisho_catalog_no: "",
      source_language: "",
      place: null,
      name: "",
      place_id: "",
      entity_status_of_proofreading:
        SystemEntityProofreadingStatus.NOT_PROOFREAD,
      internal_notes: "",
      notes: "",
      fromDoc: from
        ? { ...from, _collection: SystemEntityCollections.VERSION }
        : null,
      toDoc: to ? { ...to, _collection: SystemEntityCollections.TEAM } : null,
      attributes_metadata: initMetadata<VersionTeamConnection>(metadataFieldNames),
    };
  }, [from, to, teamContributionType, systemLanguageType, metadataFieldNames]);

  const typeBasedFields = useMemo<Array<keyof VersionTeamConnection>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (teamContributionType) {
      case SystemTeamContributionTypes.TRANSLATORS:
        return ["name", "date", "place", "place_identification_status"];
      case SystemTeamContributionTypes.REVISORS_OF_TIBETAN_TRANSLATION:
        return ["date", "place", "revision", "place_identification_status"];
      case SystemTeamContributionTypes.EARLIEST_CHINESE_TRANSLATION:
      case SystemTeamContributionTypes.CHINESE_TRANSLATION_CLOSER_TO_TIBETAN_WORK:
      case SystemTeamContributionTypes.CHINESE_TRANSLATION_CLOSER_TO_INDIC_WORK:
        return ["date", "title", "taisho_catalog_no", "source_language"];
    }
  }, [systemLanguageType, teamContributionType]);

  const showField = (field: keyof VersionTeamConnection): boolean => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return typeBasedFields.includes(field);
  };

  const languageBasedColumns = useMemo<
    MRT_ColumnDef<VersionTeamConnection, any>[]
  >(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
        return tibetanVersionTeamColumns;
      case SystemLanguages.chinese:
        return chineseVersionTeamColumns;
    }
  }, [systemLanguageType]);

  const getDisplayName = (connection: VersionTeamConnection) =>
    `${SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS[connection.system_team_contribution_type]} Version Team Connection`;

  return {
    userEnums,
    teamContributionPlaceIdentificationStatusEnum,
    chineseSourceLanguageEnum,
    revisionEnum,
    metadataFieldNames,
    defaultVersionTeam,
    getDisplayName,
    systemLanguageType,
    showField,
    languageBasedColumns,
  };
};

export default useVersionTeamConnectionLogic;
